/**
 * A ficha do associado no ticket (spec 031): campos fixos, e a descrição
 * continua sendo o registro do worker.
 *
 * O worker da PoolTrack escreve a ficha do SGR na DESCRIÇÃO (`PLACA:`,
 * `TELEFONE:`…), e o espelho do TiFlux a mantém lá. O ticket aberto à mão não
 * tem essa ficha. Agora a ficha tem 16 slots próprios (`tickets.sheet_*`), e a
 * tabela guarda SÓ o que uma pessoa escreveu:
 *
 * - `null`: ninguém escreveu, e o slot segue a origem (descrição, título,
 *   formulário);
 * - `''`: a pessoa APAGOU. Vence tudo: o telefone da descrição que alguém
 *   apagou sai do disparo, da busca e do bot;
 * - texto: o que a pessoa escreveu. Também vence tudo.
 *
 * O resto vem da descrição na hora da leitura. Por isso não há backfill, e
 * editar a ficha nunca reescreve a descrição (D4).
 *
 * UMA função decide a ficha de um ticket, `resolveTicketSheet` (D3), e roda na
 * API e no navegador. O disparo, a OS, o bot, o portal, a cotação e o nome da
 * conversa a chamam (ou chamam as camadas dela, `resolveSheetSlot` e
 * `ticketSheetPhones`). Nenhum consumidor recompõe a ordem.
 *
 * Gêmeas SQL (migration 59): `erp.ticket_plate(title, description, sheet_plate)`
 * e `erp.ticket_phones(description, requestor_phone, sheet_phone,
 * sheet_phone_alt)`. A equivalência das duas é provada numa matriz em
 * apps/api/src/modules/tickets/infrastructure/ticket-sheet-sql.db.test.ts.
 */
import { z } from 'zod';
import type { Terms } from './profiles';
import { parseSgrDescription, parseSgrTitle, type SgrSheetFields, type SgrVehicleSheet } from './sgr';

// ── Slots ───────────────────────────────────────────────────────────────────

/**
 * Os 16 slots, na ordem do formulário: o contato, o recurso e o endereço. A
 * coluna é `sheet_<slot em snake_case>` (`phoneAlt` → `sheet_phone_alt`).
 */
export const TICKET_SHEET_SLOTS = [
  'name',
  'phone',
  'phoneAlt',
  'plate',
  'brand',
  'model',
  'color',
  'year',
  'chassis',
  'street',
  'number',
  'complement',
  'neighborhood',
  'city',
  'state',
  'zip',
] as const;
export type TicketSheetSlot = (typeof TICKET_SHEET_SLOTS)[number];

/** O que a tabela guarda: por slot, null (segue a origem), '' (apagado) ou o texto. */
export type TicketSheetSlots = Record<TicketSheetSlot, string | null>;

/** A ficha de quem ninguém editou — todo ticket antes da spec 031. */
export const EMPTY_TICKET_SHEET: Readonly<TicketSheetSlots> = Object.freeze({
  name: null,
  phone: null,
  phoneAlt: null,
  plate: null,
  brand: null,
  model: null,
  color: null,
  year: null,
  chassis: null,
  street: null,
  number: null,
  complement: null,
  neighborhood: null,
  city: null,
  state: null,
  zip: null,
});

/** Algum slot foi escrito por uma pessoa (inclusive apagado)? */
export function ticketSheetEdited(sheet: TicketSheetSlots): boolean {
  return TICKET_SHEET_SLOTS.some((slot) => sheet[slot] !== null);
}

/**
 * Tamanho máximo de cada slot (§1). Os mesmos números estão nos CHECKs da
 * migration 59: a API recusa com 422 antes, e o banco segura quem escrever por
 * outro caminho.
 */
export const TICKET_SHEET_MAX: Record<TicketSheetSlot, number> = {
  name: 120,
  phone: 40,
  phoneAlt: 40,
  plate: 20,
  brand: 60,
  model: 120,
  color: 40,
  year: 20,
  chassis: 40,
  street: 200,
  number: 20,
  complement: 120,
  neighborhood: 120,
  city: 120,
  state: 2,
  zip: 9,
};

/** UF: duas letras maiúsculas (ou '' — apagado). */
export const TICKET_SHEET_STATE_RE = /^([A-Z]{2})?$/;
/** CEP: 00000-000 ou 00000000 (ou '' — apagado). */
export const TICKET_SHEET_ZIP_RE = /^([0-9]{5}-?[0-9]{3})?$/;

const slotText = (slot: TicketSheetSlot) =>
  z
    .string()
    .trim()
    .max(TICKET_SHEET_MAX[slot])
    // o Postgres não guarda NUL em text: sem isto seria 500, não 422
    .refine((v) => !v.includes(String.fromCharCode(0)), { message: 'caractere nulo' });

/**
 * O que a API aceita na ficha, no POST e no PATCH de ticket. Todo slot é
 * opcional e aceita `null` ("voltar ao da descrição") e `''` ("a pessoa
 * apagou"). O telefone é guardado como foi digitado: todo leitor já normaliza,
 * e o formato só é cobrado onde há disparo (§5).
 */
export const TicketSheetInputSchema = z
  .object({
    name: slotText('name').nullable(),
    phone: slotText('phone').nullable(),
    phoneAlt: slotText('phoneAlt').nullable(),
    plate: slotText('plate').nullable(),
    brand: slotText('brand').nullable(),
    model: slotText('model').nullable(),
    color: slotText('color').nullable(),
    year: slotText('year').nullable(),
    chassis: slotText('chassis').nullable(),
    street: slotText('street').nullable(),
    number: slotText('number').nullable(),
    complement: slotText('complement').nullable(),
    neighborhood: slotText('neighborhood').nullable(),
    city: slotText('city').nullable(),
    state: z
      .string()
      .trim()
      .toUpperCase()
      .regex(TICKET_SHEET_STATE_RE, 'UF: duas letras')
      .nullable(),
    zip: z.string().trim().regex(TICKET_SHEET_ZIP_RE, 'CEP: 00000-000').nullable(),
  })
  .partial()
  .strict();
export type TicketSheetInput = z.infer<typeof TicketSheetInputSchema>;

/** A ficha como a API a devolve no detalhe: os 16 slots, cada um null, '' ou texto. */
export const TicketSheetSchema = z.object({
  name: z.string().nullable(),
  phone: z.string().nullable(),
  phoneAlt: z.string().nullable(),
  plate: z.string().nullable(),
  brand: z.string().nullable(),
  model: z.string().nullable(),
  color: z.string().nullable(),
  year: z.string().nullable(),
  chassis: z.string().nullable(),
  street: z.string().nullable(),
  number: z.string().nullable(),
  complement: z.string().nullable(),
  neighborhood: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
}) satisfies z.ZodType<TicketSheetSlots>;

// ── Precedência (§3) ────────────────────────────────────────────────────────

/** De onde veio o valor de um slot. */
export type TicketSheetSource = 'person' | 'title' | 'description' | 'requestor' | 'equipment';

/**
 * As origens de ticket em que o FORMULÁRIO (o solicitante, o recurso ligado)
 * entra na ficha: só `web`, o ticket aberto à mão na tela do ERP. É a lista do
 * disparo desde o erp#43 (DISPATCH_REQUESTOR_PHONE_ORIGINS a reexporta): no
 * importado do TiFlux o solicitante é o placeholder de um terceiro (F-79), e
 * portal, e-mail e formulário externo foram preenchidos por gente de fora.
 */
export const TICKET_SHEET_FORM_ORIGINS: readonly string[] = ['web'];

export function ticketSheetFormLayer(createdVia: string | null | undefined): boolean {
  return createdVia != null && TICKET_SHEET_FORM_ORIGINS.includes(createdVia);
}

/**
 * A entrada da precedência. Tudo OBRIGATÓRIO de propósito, como no
 * DispatchParamTicket: um consumidor que esquecesse um campo discordaria do
 * envio sem erro nenhum. Quem não tem o formulário (o portal, o bot, o nome da
 * conversa) passa `createdVia: null`, e as camadas dele não entram.
 */
export interface TicketSheetSourceInput {
  title: string | null;
  description: string | null;
  /** `tickets.created_via` — decide se o formulário entra (ticketSheetFormLayer) */
  createdVia: string | null | undefined;
  /** o "Solicitante" (nome) do formulário */
  requestorName: string | null;
  /** o "Telefone do solicitante" do formulário, como foi digitado */
  requestorPhone: string | null;
  /** o nome do recurso ligado ao ticket (`equipments.name`) */
  equipmentName: string | null;
  /**
   * O veículo do SGR escolhido por uma pessoa (spec 032,
   * `tickets.sheet_from_sgr.vehicleSgrId`): o "Código do cadastro" do ticket
   * aberto pela placa, que não tem o `ID_SGR:` do worker na descrição. Quem não
   * mostra o código (disparo, nome da conversa, portal, bot) passa null.
   */
  sgrVehicleId: string | null;
  sheet: TicketSheetSlots;
}

export interface SheetSlotValue {
  /** null = nenhuma camada tem, ou a pessoa apagou (`source: 'person'`) */
  value: string | null;
  source: TicketSheetSource | null;
}

export interface ResolvedSheetSlot extends SheetSlotValue {
  /**
   * O que o resto da ordem daria SEM a pessoa: é o "Na descrição: X" do
   * cartão e o valor de "voltar ao da descrição". Igual ao próprio slot quando
   * ninguém o editou.
   */
  fallback: SheetSlotValue;
}

export interface SheetPhone {
  value: string;
  source: 'person' | 'description' | 'requestor';
}

export interface ResolvedTicketSheet {
  slots: Record<TicketSheetSlot, ResolvedSheetSlot>;
  /**
   * Os telefones na ordem em que o disparo os tenta (ticketSheetPhones):
   * - nenhum dos dois telefones editado: a lista do parser de hoje, sem tirar
   *   nem pôr (todas as linhas TELEFONE/TELEFONE ADICIONAL);
   * - algum editado: o telefone e o adicional efetivos, cada um com a origem;
   * - por último, só no `web`, o telefone do solicitante.
   */
  phones: SheetPhone[];
  /** metadados da origem, só leitura (não viram slot — §1) */
  sgrId: string | null;
  registeredAt: string | null;
  /** a descrição traz a ficha do worker (alguma chave conhecida) */
  recognized: boolean;
}

/** texto vazio é ausência nas camadas da origem */
const cleanText = (v: string | null | undefined): string | null => {
  const t = v?.trim() ?? '';
  return t === '' ? null : t;
};

/** placa como o disparo e a busca a comparam: maiúscula, só letras e dígitos */
export const plateKey = (v: string | null | undefined): string | null => {
  const t = (v ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return t === '' ? null : t;
};

/**
 * O nome do recurso só vale como placa quando TEM CARA de identificador: 5 a 8
 * letras e dígitos, SEM espaço, depois de tirar o hífen e subir a caixa — o
 * critério do erp#51 (PLATE_LIKE). "Rastreador do caminhão 3" e "ABC 1D23" não
 * vão para a mensagem como placa.
 */
const PLATE_LIKE = /^[A-Z0-9]{5,8}$/;
const equipmentPlate = (v: string | null): string | null => {
  const candidate = cleanText(v?.replace(/-/g, '').toUpperCase());
  return candidate !== null && PLATE_LIKE.test(candidate) ? candidate : null;
};

type Layer = [TicketSheetSource, () => string | null | undefined];

/**
 * Uma camada vale quando tem texto. Na placa, quando tem ao menos uma letra ou
 * dígito: `PLACA: -` não é placa, e a ordem segue para o título (como o
 * disparo sempre fez, que só parava numa placa não vazia DEPOIS de tirar o
 * hífen).
 */
function firstLayer(layers: readonly Layer[], present: (v: string | null) => boolean): SheetSlotValue {
  for (const [source, read] of layers) {
    const value = cleanText(read());
    if (value !== null && present(value)) return { value, source };
  }
  return { value: null, source: null };
}

interface Parsed {
  description: SgrVehicleSheet;
  title: ReturnType<typeof parseSgrTitle>;
}

function parsedOf(input: Pick<TicketSheetSourceInput, 'title' | 'description'>): Parsed {
  return { description: parseSgrDescription(input.description), title: parseSgrTitle(input.title) };
}

/** As camadas da origem de cada slot, NA ORDEM (§3), sem a pessoa. */
function originLayers(
  slot: TicketSheetSlot,
  input: TicketSheetSourceInput,
  parsed: Parsed
): readonly Layer[] {
  const form = ticketSheetFormLayer(input.createdVia);
  const d = parsed.description;
  const t = parsed.title;
  switch (slot) {
    case 'name':
      // pessoa > título > `NOME:` > solicitante (só web)
      return [
        ['title', () => t.name],
        ['description', () => d.name],
        ...(form ? ([['requestor', () => input.requestorName]] as Layer[]) : []),
      ];
    case 'plate':
      // pessoa > `PLACA:` > título > recurso com cara de placa (só web). A
      // descrição antes do título é a ordem da busca e da OS (§3).
      return [
        ['description', () => d.plate],
        ['title', () => t.plate],
        ...(form
          ? ([
              ['equipment', () => equipmentPlate(input.equipmentName)],
            ] as Layer[])
          : []),
      ];
    case 'city':
      // pessoa > `CIDADE:` > título
      return [
        ['description', () => d.city],
        ['title', () => t.city],
      ];
    case 'phone':
      return [['description', () => d.phone]];
    case 'phoneAlt':
      return [['description', () => d.phoneAlt]];
    default:
      // os outros: pessoa > descrição
      return [['description', () => d[slot]]];
  }
}

function resolveSlotWith(
  slot: TicketSheetSlot,
  input: TicketSheetSourceInput,
  parsed: Parsed,
  platePresence = true
): ResolvedSheetSlot {
  const present =
    slot === 'plate' && platePresence ? (v: string | null) => plateKey(v) !== null : () => true;
  const fallback = firstLayer(originLayers(slot, input, parsed), present);
  const person = input.sheet[slot];
  if (person === null) return { ...fallback, fallback };
  // a pessoa vence tudo, inclusive com '' (apagou)
  return { value: cleanText(person), source: 'person', fallback };
}

/** Um slot pela precedência — o que o disparo pergunta do nome e da placa. */
export function resolveSheetSlot(slot: TicketSheetSlot, input: TicketSheetSourceInput): ResolvedSheetSlot {
  return resolveSlotWith(slot, input, parsedOf(input));
}

function phonesWith(input: TicketSheetSourceInput, parsed: Parsed): SheetPhone[] {
  const out: SheetPhone[] = [];
  const push = (value: string | null, source: SheetPhone['source']) => {
    if (value !== null && !out.some((p) => p.value === value)) out.push({ value, source });
  };
  if (input.sheet.phone === null && input.sheet.phoneAlt === null) {
    // ninguém editou os telefones: a lista de hoje, sem tirar nem pôr
    for (const phone of parsed.description.phones) push(phone, 'description');
  } else {
    for (const slot of ['phone', 'phoneAlt'] as const) {
      const r = resolveSlotWith(slot, input, parsed);
      push(r.value, r.source === 'person' ? 'person' : 'description');
    }
  }
  if (ticketSheetFormLayer(input.createdVia)) push(cleanText(input.requestorPhone), 'requestor');
  return out;
}

/** Os telefones na ordem em que o disparo os tenta (ver ResolvedTicketSheet.phones). */
export function ticketSheetPhones(input: TicketSheetSourceInput): SheetPhone[] {
  return phonesWith(input, parsedOf(input));
}

/**
 * A ficha do ticket, slot a slot, com a origem de cada valor. A ordem (§3):
 *
 * | slot                | ordem                                                     |
 * |---------------------|-----------------------------------------------------------|
 * | name                | pessoa > título > `NOME:` > solicitante (só web)          |
 * | plate               | pessoa > `PLACA:` > título > recurso PLATE_LIKE (só web)  |
 * | city                | pessoa > `CIDADE:` > título                               |
 * | phone, phoneAlt     | pessoa > a linha da mesma chave                           |
 * | os outros           | pessoa > descrição                                        |
 */
export function resolveTicketSheet(input: TicketSheetSourceInput): ResolvedTicketSheet {
  const parsed = parsedOf(input);
  const slots = {} as Record<TicketSheetSlot, ResolvedSheetSlot>;
  for (const slot of TICKET_SHEET_SLOTS) slots[slot] = resolveSlotWith(slot, input, parsed);
  return {
    slots,
    phones: phonesWith(input, parsed),
    // a escolha da pessoa (spec 032) vence o registro do worker, como em todo slot
    sgrId: cleanText(input.sgrVehicleId) ?? parsed.description.sgrId ?? null,
    registeredAt: parsed.description.registeredAt ?? null,
    recognized: parsed.description.recognized,
  };
}

// ── Portal e bot (D6) ───────────────────────────────────────────────────────

/**
 * A ficha que sai para FORA da equipe — o portal da associação e a API do bot:
 * as mesmas chaves do parser (as de sempre do `GET /bot/tickets/:n`), com a
 * correção da pessoa aplicada. SÓ as camadas pessoa + descrição: o solicitante,
 * o recurso e o título nunca saem por aqui (o PortalTicketDetailSchema é o
 * "subconjunto seguro").
 */
export type PublicTicketSheet = SgrSheetFields;

/** a ordem das chaves da resposta do bot desde a spec 022 — o hash da amostra (aceite 2) depende dela */
const PUBLIC_KEYS = [
  'sgrId',
  'chassis',
  'plate',
  'brand',
  'model',
  'color',
  'year',
  'name',
  'registeredAt',
  'street',
  'number',
  'complement',
  'neighborhood',
  'city',
  'state',
  'zip',
] as const;

export const PublicTicketSheetSchema = z.object({
  sgrId: z.string().optional(),
  chassis: z.string().optional(),
  plate: z.string().optional(),
  brand: z.string().optional(),
  model: z.string().optional(),
  color: z.string().optional(),
  year: z.string().optional(),
  name: z.string().optional(),
  registeredAt: z.string().optional(),
  street: z.string().optional(),
  number: z.string().optional(),
  complement: z.string().optional(),
  neighborhood: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  phones: z.array(z.string()),
});

/**
 * A ficha para o portal e o bot, ou null quando o ticket não tem ficha nenhuma
 * (a descrição não traz a do worker e ninguém escreveu um slot com valor).
 * Calculada no servidor. Para um ticket que ninguém editou, sai IGUAL à de
 * antes da spec 031 — chave a chave, na mesma ordem.
 */
export function publicTicketSheet(input: {
  description: string | null;
  sheet: TicketSheetSlots;
}): PublicTicketSheet | null {
  const layers: TicketSheetSourceInput = {
    title: null,
    description: input.description,
    createdVia: null,
    requestorName: null,
    requestorPhone: null,
    equipmentName: null,
    sgrVehicleId: null,
    sheet: input.sheet,
  };
  // só pessoa + descrição, POR CONSTRUÇÃO: a entrada não tem título, nem
  // solicitante, nem recurso (e `createdVia: null` tira o formulário)
  const parsed = parsedOf(layers);
  const anyPersonValue = TICKET_SHEET_SLOTS.some((slot) => cleanText(input.sheet[slot]) !== null);
  if (!parsed.description.recognized && !anyPersonValue) return null;

  const out: Record<string, string | string[]> = {};
  for (const key of PUBLIC_KEYS) {
    if (key === 'sgrId' || key === 'registeredAt') {
      const v = parsed.description[key];
      if (v !== undefined) out[key] = v;
      continue;
    }
    // a placa sem a regra de presença: sem o título não há para onde cair, e
    // `PLACA: -` sai como sempre saiu (o hash da amostra do aceite 2)
    const r = resolveSlotWith(key, layers, parsed, false);
    if (r.value !== null) out[key] = r.value;
  }
  out['phones'] = phonesWith(layers, parsed).map((p) => p.value);
  return out as unknown as PublicTicketSheet;
}

// ── Rótulos (§1) ────────────────────────────────────────────────────────────

/** termo do vocabulário (forma do meio da frase) → rótulo, com a inicial maiúscula */
const cap = (term: string) => term.charAt(0).toUpperCase() + term.slice(1);

/**
 * O rótulo de cada slot. Pessoa e recurso vêm do vocabulário do tenant (spec
 * 017: "Placa", "Telefone do associado" na PoolTrack; "Identificador",
 * "Telefone do contato" no genérico); os outros são texto fixo, como no
 * sgrSheetEntries.
 */
export function ticketSheetSlotLabel(
  slot: TicketSheetSlot,
  terms: Pick<Terms, 'contact' | 'resourceId'>
): string {
  switch (slot) {
    case 'name':
      return `Nome do ${terms.contact}`;
    case 'phone':
      return `Telefone do ${terms.contact}`;
    case 'phoneAlt':
      return 'Telefone adicional';
    case 'plate':
      return cap(terms.resourceId);
    case 'brand':
      return 'Marca';
    case 'model':
      return 'Modelo';
    case 'color':
      return 'Cor';
    case 'year':
      return 'Ano';
    case 'chassis':
      return 'Chassi';
    case 'street':
      return 'Rua';
    case 'number':
      return 'Número';
    case 'complement':
      return 'Complemento';
    case 'neighborhood':
      return 'Bairro';
    case 'city':
      return 'Cidade';
    case 'state':
      return 'UF';
    case 'zip':
      return 'CEP';
  }
}

/** "da descrição", "do título"… — a origem como a tela a diz ao lado do valor. */
export const TICKET_SHEET_SOURCE_LABELS: Record<TicketSheetSource, string> = {
  person: 'editado',
  title: 'do título',
  description: 'da descrição',
  requestor: 'do solicitante',
  equipment: 'do recurso',
};

/** O campo do histórico (erp#42) de um slot: `sheet.phone`. */
export const ticketSheetHistoryField = (slot: TicketSheetSlot): string => `sheet.${slot}`;

/** `sheet.phone` → `phone`; null quando não é campo da ficha. */
export function ticketSheetSlotOfField(field: string): TicketSheetSlot | null {
  if (!field.startsWith('sheet.')) return null;
  const slot = field.slice('sheet.'.length);
  return (TICKET_SHEET_SLOTS as readonly string[]).includes(slot) ? (slot as TicketSheetSlot) : null;
}
